import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import axiosInstance from '../utils/axios';
import {
  Card, Row, Col, Button, Space, Table, Tag, Spin, Select,
  Badge, message as antMessage, Empty, Divider,
} from 'antd';
import { DatePicker } from 'antd';
import {
  HomeOutlined, UserOutlined, CarOutlined, WifiOutlined,
  FileProtectOutlined, FileTextOutlined, TeamOutlined,
  FileExcelOutlined, ReloadOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { hssColors, commonStyles } from '../config/theme';

const { Option } = Select;
const { RangePicker } = DatePicker;

// ── Tipos de reporte ───────────────────────────────────────────────────────
const TIPOS = [
  { key: 'clientes',  label: 'Clientes',          icon: UserOutlined,       color: '#3498db', endpoint: '/clientes',         desc: 'Datos de contacto, vehículos y estado de contrato' },
  { key: 'vehiculos', label: 'Vehículos',          icon: CarOutlined,        color: '#e74c3c', endpoint: '/vehiculos',         desc: 'Flota completa con tipo, servicio y GPS asignado' },
  { key: 'gps',       label: 'Inventario GPS',     icon: WifiOutlined,       color: '#16a085', endpoint: '/gps',               desc: 'Dispositivos GPS y SIM cards por estado' },
  { key: 'contratos', label: 'Contratos',          icon: FileProtectOutlined,color: '#8e44ad', endpoint: '/contratos',         desc: 'Contratos digitales firmados y pendientes' },
  { key: 'ordenes',   label: 'Órdenes de Trabajo', icon: FileTextOutlined,   color: '#f39c12', endpoint: '/ordenes',           desc: 'Instalaciones, mantenimientos y desinstalaciones' },
  { key: 'usuarios',  label: 'Usuarios Acceso',    icon: TeamOutlined,       color: '#27ae60', endpoint: '/usuarios-clientes', desc: 'Accesos al sistema de rastreo satelital' },
];

// ── Columnas de vista previa por tipo ─────────────────────────────────────
const COLUMNAS = {
  clientes: [
    { title: 'DNI',      dataIndex: 'dni',      key: 'dni',      width: 90 },
    { title: 'Nombre',   dataIndex: 'nombre',   key: 'nombre' },
    { title: 'Apellido', dataIndex: 'apellido', key: 'apellido' },
    { title: 'Celular',  dataIndex: 'celular',  key: 'celular',  render: v => v || '—' },
    { title: 'Email',    dataIndex: 'email',    key: 'email',    render: v => v || '—' },
    { title: 'Vehículos', dataIndex: 'total_vehiculos', key: 'tv', align: 'center',
      render: v => <Tag color="blue">{v}</Tag> },
  ],
  vehiculos: [
    { title: 'Chapa',      dataIndex: 'chapa',      key: 'chapa',  width: 90 },
    { title: 'Marca',      dataIndex: 'marca',      key: 'marca' },
    { title: 'Modelo',     dataIndex: 'modelo',     key: 'modelo' },
    { title: 'Nombre GPS', dataIndex: 'nombre_gps', key: 'nombre_gps', render: v => v || '—' },
    { title: 'Tipo',       dataIndex: 'tipo',       key: 'tipo',   render: v => v || '—' },
    { title: 'Servicio',   dataIndex: 'servicio',   key: 'servicio',
      render: v => v ? <Tag color={v==='RASTREO'?'blue':v==='VISION'?'purple':'green'}>{v}</Tag> : '—' },
    { title: 'GPS IMEI',   dataIndex: 'gps',        key: 'gps',
      render: v => v || <span style={{ color: '#ccc' }}>Sin GPS</span> },
  ],
  gps: [
    { title: 'Marca',    dataIndex: 'marca',         key: 'marca' },
    { title: 'Modelo',   dataIndex: 'modelo',        key: 'modelo' },
    { title: 'IMEI',     dataIndex: 'imei',          key: 'imei' },
    { title: 'N° Línea', dataIndex: 'numero_linea',  key: 'nl',    render: v => v || '—' },
    { title: 'Estado',   dataIndex: 'estado',        key: 'estado',
      render: v => <Tag color={v==='instalado'?'blue':v==='stock'?'green':'default'}>{v}</Tag> },
    { title: 'Vehículo', dataIndex: 'vehiculo',      key: 'veh',   render: v => v || '—' },
  ],
  contratos: [
    { title: 'ID',            dataIndex: 'id',                  key: 'id',    width: 55 },
    { title: 'Representante', dataIndex: 'cliente_representante', key: 'rep' },
    { title: 'CI/RUC',        dataIndex: 'cliente_ci_ruc',       key: 'ci' },
    { title: 'Tipo',          dataIndex: 'tipo_contrato',        key: 'tipo',
      render: v => <Tag color={v==='RASTREO'?'blue':v==='VISION'?'purple':'green'}>{v}</Tag> },
    { title: 'Estado',        dataIndex: 'estado',              key: 'estado',
      render: v => <Tag color={v==='FIRMADO'?'green':'orange'}>{v}</Tag> },
    { title: 'Vehículo',      dataIndex: 'vehiculo_chapa',      key: 'chapa' },
    { title: 'Monto',         dataIndex: 'servicio_monto',      key: 'monto',
      render: v => v ? `Gs. ${Number(v).toLocaleString()}` : '—' },
  ],
  ordenes: [
    { title: 'ID',      dataIndex: 'id',     key: 'id',    width: 55 },
    { title: 'Tipo',    dataIndex: 'tipo',   key: 'tipo' },
    { title: 'Estado',  dataIndex: 'estado', key: 'estado',
      render: v => <Tag color={v==='COMPLETADO'?'green':v==='CANCELADO'?'red':'orange'}>{v}</Tag> },
    { title: 'Vehículo', key: 'veh',    render: (_, r) => r.vehiculo?.chapa || r.vehiculo_chapa || '—' },
    { title: 'Cliente',  key: 'cli',    render: (_, r) => r.vehiculo?.cliente ? `${r.vehiculo.cliente.nombre} ${r.vehiculo.cliente.apellido}` : '—' },
    { title: 'Técnico',  key: 'tec',    render: (_, r) => r.responsable?.nombre || '—' },
    { title: 'Fecha',   dataIndex: 'fecha_creacion', key: 'fecha',
      render: v => v ? new Date(v).toLocaleDateString('es-PY') : '—' },
  ],
  usuarios: [
    { title: 'Usuario',      dataIndex: 'usuario',        key: 'usuario' },
    { title: 'Cliente',      dataIndex: 'cliente_nombre', key: 'cli',   render: v => v || <span style={{ color: '#ccc' }}>Sin asignar</span> },
    { title: 'Vehículos',    key: 'vehs', render: (_, r) => r.vehiculos?.map(v => v.chapa).join(', ') || '—' },
    { title: 'Observaciones',dataIndex: 'observaciones',  key: 'obs',   render: v => v || '—' },
  ],
};

// ── Campos Excel por tipo ─────────────────────────────────────────────────
const toExcel = {
  clientes:  c => ({ 'DNI': c.dni, 'Nombre': c.nombre, 'Apellido': c.apellido, 'RUC': c.ruc||'', 'Celular': c.celular||'', 'Email': c.email||'', 'Domicilio': c.domicilio||'', 'Total Vehículos': c.total_vehiculos, 'Rastreo': c.vehiculos_rastreo, 'Vision': c.vehiculos_vision }),
  vehiculos: v => ({ 'Chapa': v.chapa, 'Marca': v.marca, 'Modelo': v.modelo, 'Nombre GPS': v.nombre_gps||'', 'Año': v.ano||'', 'Color': v.color||'', 'Tipo': v.tipo||'', 'Servicio': v.servicio||'', 'GPS IMEI': v.gps||'', 'Cliente DNI': v.cliente_dni||'' }),
  gps:       g => ({ 'Marca': g.marca, 'Modelo': g.modelo, 'IMEI': g.imei, 'N° Línea': g.numero_linea||'', 'Estado': g.estado, 'Vehículo': g.vehiculo||'', 'Chapa': g.vehiculo_chapa||'' }),
  contratos: c => ({ 'ID': c.id, 'Cliente DNI': c.cliente_dni||'', 'Representante': c.cliente_representante||'', 'CI/RUC': c.cliente_ci_ruc||'', 'Tipo': c.tipo_contrato, 'Estado': c.estado, 'Vehículo': c.vehiculo_chapa||'', 'Marca': c.vehiculo_marca||'', 'Modelo': c.vehiculo_modelo||'', 'Monto': c.servicio_monto||'', 'Forma Pago': c.servicio_forma_pago||'', 'Vigencia': c.vigencia||'', 'Fecha Creación': c.created_at ? new Date(c.created_at).toLocaleDateString('es-PY') : '', 'Fecha Firma': c.signed_at ? new Date(c.signed_at).toLocaleDateString('es-PY') : '' }),
  ordenes:   o => ({ 'ID': o.id, 'Tipo': o.tipo, 'Estado': o.estado, 'Vehículo': o.vehiculo?.chapa||o.vehiculo_chapa||'', 'Cliente': o.vehiculo?.cliente ? `${o.vehiculo.cliente.nombre} ${o.vehiculo.cliente.apellido}` : '', 'Técnico': o.responsable?.nombre||'', 'Observaciones': o.observaciones||'', 'Fecha Creación': o.fecha_creacion ? new Date(o.fecha_creacion).toLocaleDateString('es-PY') : '' }),
  usuarios:  u => ({ 'Usuario': u.usuario, 'Cliente': u.cliente_nombre||'Sin asignar', 'DNI Cliente': u.cliente_dni||'', 'Vehículos': u.vehiculos?.map(v => v.chapa).join(', ')||'', 'Observaciones': u.observaciones||'' }),
};

// ── Filtros disponibles por tipo ─────────────────────────────────────────
const FILTROS_DEF = {
  clientes: [
    { key: 'conVehiculos', label: 'Vehículos', opts: [['todos','Todos'],['con','Con vehículos'],['sin','Sin vehículos']] },
    { key: 'conContrato',  label: 'Contrato firmado', opts: [['todos','Todos'],['con','Con contrato'],['sin','Sin contrato']] },
  ],
  vehiculos: [
    { key: 'cliente',  label: 'Cliente',  type: 'select_search' },
    { key: 'tipo',     label: 'Tipo',     opts: [['todos','Todos'],['automovil','Automóvil'],['camion','Camión'],['camioneta','Camioneta'],['moto','Moto'],['tractor','Tractor'],['acoplado','Acoplado'],['semirremolque','Semirremolque']] },
    { key: 'servicio', label: 'Servicio', opts: [['todos','Todos'],['RASTREO','Rastreo'],['VISION','Vision'],['RASTREO_VISION','Rastreo + Vision']] },
    { key: 'conGPS',   label: 'GPS',      opts: [['todos','Todos'],['con','Con GPS'],['sin','Sin GPS']] },
  ],
  gps: [
    { key: 'estado', label: 'Estado', opts: [['todos','Todos'],['stock','En stock'],['instalado','Instalados'],['baja','De baja']] },
  ],
  contratos: [
    { key: 'estado',       label: 'Estado', opts: [['todos','Todos'],['FIRMADO','Firmado'],['PENDIENTE','Pendiente']] },
    { key: 'tipo_contrato',label: 'Tipo',   opts: [['todos','Todos'],['RASTREO','Rastreo'],['VISION','Vision'],['RASTREO_VISION','Rastreo + Vision']] },
  ],
  ordenes: [
    { key: 'estado', label: 'Estado', opts: [['todos','Todos'],['ACTIVO','Activo'],['COMPLETADO','Completado'],['CANCELADO','Cancelado']] },
    { key: 'tipo',   label: 'Tipo',   opts: [['todos','Todos'],['Instalacion','Instalación'],['Desinstalacion','Desinstalación'],['Mantenimiento','Mantenimiento']] },
    { key: 'fechas', label: 'Rango de fechas', type: 'daterange' },
  ],
  usuarios: [
    { key: 'conCliente', label: 'Cliente asignado', opts: [['todos','Todos'],['con','Con cliente'],['sin','Sin cliente']] },
  ],
};

// ── Función de filtrado ────────────────────────────────────────────────────
const filtrar = (datos, tipo, filtros, contratosSet) => {
  let r = datos;
  switch (tipo) {
    case 'clientes':
      if (filtros.conVehiculos === 'con') r = r.filter(c => c.total_vehiculos > 0);
      if (filtros.conVehiculos === 'sin') r = r.filter(c => c.total_vehiculos === 0);
      if (filtros.conContrato === 'con')  r = r.filter(c => contratosSet.has(c.dni));
      if (filtros.conContrato === 'sin')  r = r.filter(c => !contratosSet.has(c.dni));
      break;
    case 'vehiculos':
      if (filtros.cliente && filtros.cliente !== 'todos') r = r.filter(v => v.cliente_dni === filtros.cliente);
      if (filtros.tipo !== 'todos')     r = r.filter(v => v.tipo === filtros.tipo);
      if (filtros.servicio !== 'todos') r = r.filter(v => v.servicio === filtros.servicio);
      if (filtros.conGPS === 'con')     r = r.filter(v => v.gps);
      if (filtros.conGPS === 'sin')     r = r.filter(v => !v.gps);
      break;
    case 'gps':
      if (filtros.estado !== 'todos') r = r.filter(g => g.estado === filtros.estado);
      break;
    case 'contratos':
      if (filtros.estado !== 'todos')       r = r.filter(c => c.estado === filtros.estado);
      if (filtros.tipo_contrato !== 'todos') r = r.filter(c => c.tipo_contrato === filtros.tipo_contrato);
      break;
    case 'ordenes':
      if (filtros.estado !== 'todos') r = r.filter(o => o.estado === filtros.estado);
      if (filtros.tipo !== 'todos')   r = r.filter(o => o.tipo === filtros.tipo);
      if (filtros.fechas?.[0]) {
        const desde = filtros.fechas[0].startOf('day').toDate();
        const hasta = filtros.fechas[1].endOf('day').toDate();
        r = r.filter(o => {
          const f = new Date(o.fecha_creacion);
          return f >= desde && f <= hasta;
        });
      }
      break;
    case 'usuarios':
      if (filtros.conCliente === 'con') r = r.filter(u => u.cliente_dni);
      if (filtros.conCliente === 'sin') r = r.filter(u => !u.cliente_dni);
      break;
    default: break;
  }
  return r;
};

// ── Valores de filtro por defecto ─────────────────────────────────────────
const filtrosDefault = tipo => {
  const defs = {};
  (FILTROS_DEF[tipo] || []).forEach(f => { defs[f.key] = (f.type === 'daterange' || f.type === 'select_search') ? null : 'todos'; });
  return defs;
};

// ══════════════════════════════════════════════════════════════════════════
const Reportes = () => {
  const navigate = useNavigate();
  const [tipoActual, setTipoActual]   = useState(null);
  const [datosRaw, setDatosRaw]       = useState([]);
  const [contratosSet, setContratosSet] = useState(new Set());
  const [filtros, setFiltros]         = useState({});
  const [cargando, setCargando]       = useState(false);
  const [exportando, setExportando]   = useState(false);
  const [clientesMap, setClientesMap] = useState(new Map());
  const [clientesLista, setClientesLista] = useState([]);

  const seleccionar = async (tipo) => {
    if (tipo === tipoActual) return;
    setTipoActual(tipo);
    setFiltros(filtrosDefault(tipo));
    setDatosRaw([]);
    setCargando(true);
    try {
      const config = TIPOS.find(t => t.key === tipo);
      const res = await axiosInstance.get(config.endpoint);
      setDatosRaw(res.data);

      if (tipo === 'clientes') {
        const cRes = await axiosInstance.get('/contratos');
        const firmados = new Set(
          cRes.data.filter(c => c.estado === 'FIRMADO').map(c => c.cliente_dni)
        );
        setContratosSet(firmados);
      }

      if (tipo === 'vehiculos') {
        const cRes = await axiosInstance.get('/clientes');
        const map = new Map(cRes.data.map(c => [c.dni, `${c.nombre} ${c.apellido}`]));
        setClientesMap(map);
        const lista = cRes.data
          .filter(c => c.total_vehiculos > 0)
          .sort((a, b) => `${a.nombre} ${a.apellido}`.localeCompare(`${b.nombre} ${b.apellido}`))
          .map(c => ({ value: c.dni, label: `${c.nombre} ${c.apellido}` }));
        setClientesLista(lista);
      }
    } catch {
      antMessage.error('No se pudo cargar los datos.');
    } finally {
      setCargando(false);
    }
  };

  const datosFiltrados = useMemo(
    () => filtrar(datosRaw, tipoActual, filtros, contratosSet),
    [datosRaw, tipoActual, filtros, contratosSet]
  );

  const columnasActuales = useMemo(() => {
    if (tipoActual === 'vehiculos') {
      return [
        ...COLUMNAS.vehiculos,
        { title: 'Cliente', dataIndex: 'cliente_dni', key: 'cliente',
          render: v => clientesMap.get(v) || v || '—' },
      ];
    }
    return COLUMNAS[tipoActual] || [];
  }, [tipoActual, clientesMap]);

  const exportar = () => {
    if (datosFiltrados.length === 0) return;
    setExportando(true);
    try {
      const tipo = TIPOS.find(t => t.key === tipoActual);
      const mapper = tipoActual === 'vehiculos'
        ? v => ({ ...toExcel.vehiculos(v), 'Cliente': clientesMap.get(v.cliente_dni) || v.cliente_dni || '' })
        : toExcel[tipoActual];
      const ws = XLSX.utils.json_to_sheet(datosFiltrados.map(mapper));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, tipo.label);
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(
        new Blob([buf], { type: 'application/octet-stream' }),
        `HSS_${tipo.label.replace(/ /g,'_')}_${new Date().toISOString().slice(0,10)}.xlsx`
      );
    } catch {
      antMessage.error('Error al exportar.');
    } finally {
      setExportando(false);
    }
  };

  const setFiltro = (key, val) => setFiltros(prev => ({ ...prev, [key]: val }));
  const tipoConfig = TIPOS.find(t => t.key === tipoActual);

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <FileExcelOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Reportes</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>Generá y exportá listas personalizadas</p>
            </div>
          </div>
          <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>Inicio</Button>
        </div>
      </div>

      <div style={{ padding: '28px 32px', flex: 1 }}>

        {/* ── Selector de tipo ─────────────────────────────────────────── */}
        <p style={{ margin: '0 0 16px', fontSize: 14, color: hssColors.textSecondary }}>
          Seleccioná el tipo de información que querés exportar:
        </p>
        <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
          {TIPOS.map(tipo => {
            const Icono = tipo.icon;
            const activo = tipoActual === tipo.key;
            return (
              <Col xs={12} sm={8} lg={4} key={tipo.key}>
                <Card
                  hoverable
                  onClick={() => seleccionar(tipo.key)}
                  style={{
                    borderRadius: 12, cursor: 'pointer', textAlign: 'center',
                    border: activo ? `2px solid ${tipo.color}` : '2px solid transparent',
                    boxShadow: activo ? `0 0 0 3px ${tipo.color}22` : commonStyles.shadowMd,
                    background: activo ? `${tipo.color}08` : '#fff',
                    transition: 'all 0.2s',
                  }}
                  styles={{ body: { padding: '20px 12px' } }}
                >
                  <Icono style={{ fontSize: 32, color: tipo.color, marginBottom: 8 }} />
                  <div style={{ fontWeight: 700, fontSize: 13, color: activo ? tipo.color : hssColors.textPrimary }}>
                    {tipo.label}
                  </div>
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 4, lineHeight: 1.3 }}>
                    {tipo.desc}
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>

        {/* ── Panel de filtros y resultado ─────────────────────────────── */}
        {tipoActual && (
          <Card
            style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}
            styles={{ body: { padding: 24 } }}
          >
            {/* Título y controles */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {tipoConfig && <tipoConfig.icon style={{ fontSize: 22, color: tipoConfig.color }} />}
                <span style={{ fontSize: 18, fontWeight: 700, color: hssColors.textPrimary }}>{tipoConfig?.label}</span>
                <Badge
                  count={cargando ? '…' : datosFiltrados.length}
                  style={{ background: tipoConfig?.color, fontSize: 13, fontWeight: 700 }}
                  overflowCount={9999}
                />
                <span style={{ fontSize: 13, color: '#888' }}>registros</span>
              </div>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => seleccionar(tipoActual)}
                  disabled={cargando}
                >
                  Actualizar
                </Button>
                <Button
                  type="primary"
                  icon={<FileExcelOutlined />}
                  style={{ background: '#1D6F42', borderColor: '#1D6F42', fontWeight: 600 }}
                  onClick={exportar}
                  loading={exportando}
                  disabled={datosFiltrados.length === 0}
                >
                  Exportar Excel ({datosFiltrados.length})
                </Button>
              </Space>
            </div>

            {/* Filtros */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 20, padding: '14px 16px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#555', alignSelf: 'center' }}>Filtros:</span>
              {(FILTROS_DEF[tipoActual] || []).map(f => (
                f.type === 'daterange' ? (
                  <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: '#666' }}>{f.label}:</span>
                    <RangePicker
                      format="DD/MM/YYYY"
                      placeholder={['Desde', 'Hasta']}
                      onChange={val => setFiltro(f.key, val)}
                      style={{ width: 240 }}
                    />
                  </div>
                ) : f.type === 'select_search' ? (
                  <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: '#666' }}>{f.label}:</span>
                    <Select
                      showSearch
                      allowClear
                      placeholder="Todos los clientes"
                      value={filtros[f.key] || null}
                      onChange={val => setFiltro(f.key, val || null)}
                      style={{ width: 220 }}
                      size="middle"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={clientesLista}
                    />
                  </div>
                ) : (
                  <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: '#666' }}>{f.label}:</span>
                    <Select
                      value={filtros[f.key] || 'todos'}
                      onChange={val => setFiltro(f.key, val)}
                      style={{ width: 160 }}
                      size="middle"
                    >
                      {f.opts.map(([val, lbl]) => (
                        <Option key={val} value={val}>{lbl}</Option>
                      ))}
                    </Select>
                  </div>
                )
              ))}
            </div>

            {/* Tabla de vista previa */}
            <Divider style={{ margin: '0 0 16px' }} />
            <div style={{ fontSize: 12, color: '#aaa', marginBottom: 8 }}>
              Vista previa — primeros 10 registros. El Excel exporta todos ({datosFiltrados.length}).
            </div>
            {cargando ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" tip="Cargando datos..." />
              </div>
            ) : datosFiltrados.length === 0 ? (
              <Empty description="No hay registros con los filtros aplicados" />
            ) : (
              <Table
                columns={columnasActuales}
                dataSource={datosFiltrados.slice(0, 10)}
                rowKey={(r, i) => r.id || r.dni || r.chapa || r.imei || r.usuario || i}
                size="small"
                pagination={false}
                scroll={{ x: true }}
                style={{ borderRadius: 8, overflow: 'hidden' }}
              />
            )}
          </Card>
        )}

        {/* Estado inicial: ningún tipo seleccionado */}
        {!tipoActual && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#bbb' }}>
            <FileExcelOutlined style={{ fontSize: 64, marginBottom: 16 }} />
            <p style={{ fontSize: 16 }}>Seleccioná un tipo de reporte para comenzar</p>
          </div>
        )}

      </div>
      <Footer />
    </div>
  );
};

export default Reportes;
